import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { createPortal, flushSync } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";

import { NTH_PURCHASE_CAMPAIGN_QUERY_KEY } from "@frontend-common/hooks/use-nth-purchase-campaign";
import { cn } from "@/lib/utils";
import { Footer, Page } from "@/components/page";
import { LoadingSpinner } from "@/components/spinner";
import { OfflineWarning } from "@/components/offline-warning";
import { useMuiModal } from "@/components/modal";
import { ErrorModal } from "@/lib/api";
import { api, type OrderStatus } from "@/lib/server";
import { getToken } from "@/hooks/use-token";
import { useShop } from "@/hooks/use-shop";
import { getActiveCoupon } from "@/hooks/use-coupon";
import type { Machine } from "@/hooks/use-shop-details";
import { recordTrackedEvent } from "@/lib/kiosk-session-tracking";
import { useApplyPayment } from "@/hooks/use-apply-payment";
import { useRemovePayment } from "@/hooks/use-remove-payment";
import { AudioSlot, useAudioSlot } from "@/features/audio/kiosk-audio";
import {
  createPayment,
  getPaymentFromSessionStorage,
  type PaymentType,
} from "@/features/payment/utils";
import {
  PaymentMethodChoice,
  PaymentMethodHelpText,
  type PaymentMethod,
} from "@/features/payment/payment-method-choice";
import {
  CardPaymentHelpText,
  CardPaymentInstructions,
} from "@/features/payment/card-payment-instructions";
import {
  BalancePaymentConfirm,
  BalancePaymentHelpText,
  type Price,
} from "@/features/payment/balance-payment-confirm";
import {
  CashPaymentBody,
  CashPaymentFooter,
  CashPaymentOutcomeModals,
  useCashPayment,
} from "@/features/payment-cash/cash-payment";
import {
  GuideKeyframes,
  guideHeader,
  guideStatus,
} from "@/features/wash-start-guide/start-guide-parts";
import {
  isStartFailed,
  orderStatusQuery,
} from "@/features/wash-start-guide/order-status";
import { WashStartGuideContent } from "@/features/wash-start-guide/wash-start-guide-screen";
import { DryStartGuideContent } from "@/features/wash-start-guide/dry-start-guide-screen";
import { audioSlotFor } from "@/features/wash-start-guide/wash-start-guide";
import {
  FlowBack,
  FlowBackSlot,
  FlowHeader,
  FlowHeaderActions,
  FLOW_HEADER_OVERLAP,
  type FlowMachine,
  type FlowMachineKind,
} from "@/features/payment-flow/flow-header";
import {
  StepTransition,
  useStepLeaving,
} from "@/features/payment-flow/step-transition";
import {
  createMachineMorph,
  type MachineMorph,
} from "@frontend-common/start-guide/machine-morph";
import {
  MachineGrid,
  MachineSelectHelpText,
} from "@/routes/machine-selector-screen.$machine-type";
import {
  DryerBundleChoice,
  type ChosenDryerBundle,
} from "@/features/dryer-bundles/dryer-bundle-choice";
import {
  CouponCodeForm,
  CouponHelpText,
} from "@/features/coupons/coupon-code-form";

// The whole purchase of one machine on one screen: machine → dryer program
// (dryers with bundles) → machine set up before paying (washer: door and
// program; dryer: door and temperature) → payment method → card / cash /
// balance → start guide → started or failed. One header shows the machine
// throughout: the tapped machine card flies into it. The steps slide into each
// other. Leaves the screen only when the purchase is over (or for the invoice
// form).

type FlowMachineWithId = FlowMachine & {
  id: number;
  /** The user's own reservation: no "Sikeres fizetés!" beat before its start guide. */
  reserved: boolean;
};

/** The machine (and dryer program) chosen, before its payment is created. */
type Choice = {
  machine: FlowMachineWithId;
  dryerBundle: ChosenDryerBundle | null;
};

/** What's being bought, once the payment for it is created. */
export type Purchase = Choice & {
  payment: PaymentType;
  activeCouponCode: string | null;
};

// Each step carries its purchase, so a step that's sliding out still renders
// what it showed.
type Step =
  // `chosen`: the tapped card flies into the header while its payment is created
  | { name: "machine"; chosen?: FlowMachineWithId }
  | { name: "bundle"; machine: FlowMachineWithId }
  // the machine is set up before paying, so it starts right after the
  // payment; `purchase`: a reservation's, created before the flow
  | { name: "prepare"; choice: Choice; purchase?: Purchase }
  | { name: "select"; purchase: Purchase }
  | { name: "coupon"; purchase: Purchase }
  | { name: "card"; purchase: Purchase }
  | { name: "cash"; purchase: Purchase }
  | { name: "balance"; purchase: Purchase; price: Price }
  // the start guide, opened by a beat to say the money went through
  | { name: "paid"; purchase: Purchase; orderId: string | null }
  // `orderId` null: an older cash-agent, no start guide
  | { name: "started"; purchase: Purchase; orderId: string | null };

const STEP_INDEX: Record<Step["name"], number> = {
  machine: 0,
  bundle: 1,
  prepare: 2,
  select: 3,
  coupon: 4,
  card: 5,
  cash: 5,
  balance: 5,
  paid: 6,
  started: 7,
};

/**
 * Opens the flow at this step instead of the machine choice: for stories that
 * show one step on its own (e.g. every state of the cash payment).
 */
export const PaymentFlowStartStep = createContext<Step | null>(null);

// the machine waits this long for the door + program before we give up
const START_WINDOW_SECONDS = 180;

export function PaymentFlow({
  kind,
  reservedPurchase,
}: {
  kind: FlowMachineKind;
  /** A reserved machine picked on the reservations screen: starts at the payment method. */
  reservedPurchase?: Purchase | null;
}) {
  const startStep = useContext(PaymentFlowStartStep);
  const [step, setStep] = useState<Step>(
    () =>
      startStep ??
      (reservedPurchase
        ? {
            name: "prepare",
            choice: reservedPurchase,
            purchase: reservedPurchase,
          }
        : { name: "machine" }),
  );
  // kept in state: its `into` is the badge's ref and must stay the same
  const [morph] = useState(createMachineMorph);
  // polled from the success beat on, so the guide has its status right away
  const orderId =
    step.name === "paid" || step.name === "started" ? step.orderId : null;
  const status = useQuery({
    ...orderStatusQuery(orderId ?? ""),
    enabled: !!orderId,
  });
  const [startTimedOut, setStartTimedOut] = useState(false);
  const flow = usePaymentFlow({ setStep, morph });

  return (
    // nobody touches the kiosk while paying by card or cash, or at the machine
    <Page
      className="flex flex-col text-bub-dark-blue"
      skipInactvityModal={["card", "cash", "paid", "started"].includes(step.name)}
    >
      <GuideKeyframes />
      <FlowBackSlot>
        <FlowHeader
          kind={kind}
          badgeRef={morph.into}
          // only the prepare and start guide steps reach up into its wave
          overlap={false}
          actions={<FlowHeaderActions />}
          {...stepHeader(kind, step, status.data, startTimedOut)}
        />
        {/* the card step itself is the wait for the terminal; the flying card
            is the wait for its payment */}
        {flow.pending && step.name !== "card" && step.name !== "machine" && (
          <LoadingSpinner />
        )}
        <main className="relative flex-1 flex flex-col px-12 pb-12">
          <StepTransition
            step={step}
            // the paid beat is part of the start guide: no slide between them
            stepKey={step.name === "paid" ? "started" : step.name}
            index={STEP_INDEX[step.name]}
          >
            {(step) =>
              step.name === "machine" ? (
                <MachineStep
                  kind={kind}
                  chosen={step.chosen}
                  onChoose={flow.chooseMachine}
                  onBack={flow.exit}
                />
              ) : step.name === "bundle" ? (
                <BundleStep
                  machineId={step.machine.id}
                  onChoose={(bundle) => flow.chooseBundle(step.machine, bundle)}
                  onBack={() => setStep({ name: "machine" })}
                />
              ) : step.name === "select" ? (
                <SelectStep
                  payment={step.purchase.payment}
                  machineId={step.purchase.machine.id}
                  activeCouponCode={step.purchase.activeCouponCode}
                  pending={flow.pending}
                  onReloadPayment={() => flow.reload(step.purchase)}
                  onChoose={(method) => flow.start(step.purchase, method)}
                  onEnterCoupon={() =>
                    setStep({ name: "coupon", purchase: step.purchase })
                  }
                  onBack={() => flow.backFromSelect(step.purchase)}
                />
              ) : step.name === "coupon" ? (
                <CouponStep
                  machineId={step.purchase.machine.id}
                  dryerBundleId={step.purchase.payment.dryerBundleId}
                  onBack={() =>
                    setStep({ name: "select", purchase: step.purchase })
                  }
                  // the payment is created again with the coupon's discount
                  onApplied={() => flow.reload(step.purchase)}
                />
              ) : step.name === "prepare" ? (
                <PrepareStep
                  machine={step.choice.machine}
                  onPrepared={() => flow.prepared(step.choice, step.purchase)}
                  onBack={() => flow.backFromPrepare(step.choice, step.purchase)}
                />
              ) : step.name === "card" ? (
                <StepBody
                  footer={<Footer helpText={<CardPaymentHelpText />} />}
                >
                  <AudioSlot slot="page.payment-card" />
                  <CardPaymentInstructions
                    price={step.purchase.payment.price}
                    showTitle={false}
                  />
                </StepBody>
              ) : step.name === "cash" ? (
                <CashStep
                  payment={step.purchase.payment}
                  onPaid={() => flow.paid(step.purchase)}
                  onBackToSelector={() => flow.reload(step.purchase)}
                />
              ) : step.name === "balance" ? (
                <StepBody
                  back={{
                    onClick: () =>
                      setStep({ name: "select", purchase: step.purchase }),
                  }}
                  footer={<Footer helpText={<BalancePaymentHelpText />} />}
                >
                  <AudioSlot slot="page.payment-balance" />
                  <BalancePaymentConfirm
                    price={step.price}
                    pending={flow.pending}
                    showTitle={false}
                    onConfirm={() => flow.confirmBalance(step.purchase)}
                  />
                </StepBody>
              ) : !step.orderId || status.isError ? (
                // no order to follow: today's invoice question instead of the guide
                <Navigate to="/invoice-question-screen" />
              ) : !status.data ? (
                <LoadingSpinner />
              ) : (
                <StartGuide
                  machine={step.purchase.machine}
                  onPaidBeatEnd={
                    step.name === "paid"
                      ? () =>
                          setStep({
                            name: "started",
                            purchase: step.purchase,
                            orderId: step.orderId,
                          })
                      : undefined
                  }
                  payment={step.purchase.payment}
                  status={status.data}
                  timedOut={startTimedOut}
                  onStartTimeout={() => setStartTimedOut(true)}
                />
              )
            }
          </StepTransition>
        </main>
      </FlowBackSlot>
    </Page>
  );
}

/**
 * The purchase for a reserved machine chosen outside the flow (the
 * reservations screen). A reservation runs its reserved program, so no dryer
 * program to pick.
 */
export async function createReservedPurchase(
  machineId: number,
): Promise<Purchase> {
  const coupon = getActiveCoupon();
  const [machine, payment] = await Promise.all([
    api.machine(machineId),
    createPayment(machineId, undefined, coupon?.couponKey),
  ]);
  return {
    machine: {
      id: machine.id,
      number: machine.storePlace,
      kind: machine.machineType === "szaritogep" ? "dryer" : "washer",
      capacity: machine.capacity,
      furFriendly: machine.furFriendly,
      // the reservations screen lists only the user's reserved machines
      reserved: true,
    },
    dryerBundle: null,
    payment,
    activeCouponCode: coupon?.code ?? null,
  };
}

function toFlowMachine(machine: Machine): FlowMachineWithId {
  return {
    id: machine.id,
    number: machine.storePlace,
    kind: machine.machineType === "szaritogep" ? "dryer" : "washer",
    capacity: machine.capacity,
    furFriendly: machine.furFriendly,
    reserved: machine.state === "AVAILABLE_BY_RESERVATION",
  };
}

// same as the machine selector screen: a reservation's dryer runs its reserved program
function needsDryerBundle(machine: Machine) {
  return (
    machine.machineType === "szaritogep" &&
    // merged in from machines-state; the shop details type doesn't know it
    !!(machine as { dryerBundles?: unknown[] }).dryerBundles?.length &&
    machine.state !== "AVAILABLE_BY_RESERVATION"
  );
}

/** Moves between the steps and makes the payment calls on the way. */
function usePaymentFlow({
  setStep,
  morph,
}: {
  setStep: (step: Step) => void;
  morph: MachineMorph;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showModal } = useMuiModal();
  const removePayment = useRemovePayment();
  const createPurchase = useMutation({
    mutationFn: async ({
      machine,
      dryerBundle,
    }: Choice): Promise<Purchase> => {
      const coupon = getActiveCoupon();
      const payment = await createPayment(
        machine.id,
        dryerBundle?.id,
        coupon?.couponKey,
      );
      return {
        machine,
        dryerBundle,
        payment,
        activeCouponCode: coupon?.code ?? null,
      };
    },
    // like the old payment screens' loader: no payment, start over
    onError: (error) => {
      console.error(error);
      navigate({ to: "/start-screen", reloadDocument: true });
    },
  });
  const purchase = (choice: Choice) =>
    createPurchase.mutateAsync(choice).catch(() => null);
  // creates the payment (again) and opens the method choice with it
  const select = async (choice: Choice) => {
    const next = await purchase(choice);
    if (next) setStep({ name: "select", purchase: next });
  };
  // apply() goes back to the method choice itself, with the purchase it failed
  const applyPayment = useApplyPayment({ onFailed: () => {} });
  const apply = (
    purchase: Purchase,
    purchaseType: Parameters<typeof applyPayment.mutate>[0]["purchaseType"],
  ) =>
    applyPayment
      .mutateAsync({ payment: purchase.payment, purchaseType })
      .then(
        () => true,
        // useApplyPayment showed the error and removed the payment
        async () => {
          await select(purchase);
          return false;
        },
      );
  const checkBalance = useMutation({
    mutationFn: async (purchase: Purchase) => {
      const response = await api.checkBalancePayment({
        kioskPaymentNonceKey: purchase.payment.kioskPaymentNonceKey,
      });
      if (response.type === "error" || !response.data) {
        showModal(<ErrorModal errorKey={response.error ?? "unknown"} />);
        throw new Error(response.error ?? "check balance payment failed");
      }
      return response.data;
    },
    onSuccess: (price, purchase) =>
      setStep({ name: "balance", purchase, price }),
  });
  const paid = (purchase: Purchase) => {
    // the purchase used up any nth-purchase-campaign hold
    void queryClient.invalidateQueries({
      queryKey: NTH_PURCHASE_CAMPAIGN_QUERY_KEY,
    });
    // the header shows the balance, which a balance payment just lowered
    void queryClient.invalidateQueries({ queryKey: ["user"] });
    setStep({
      // a reserved machine goes straight to its start guide
      name: purchase.machine.reserved ? "started" : "paid",
      purchase,
      orderId: getPaymentFromSessionStorage()?.orderId ?? null,
    });
  };

  return {
    pending:
      createPurchase.isPending ||
      applyPayment.isPending ||
      checkBalance.isPending,
    chooseMachine: async (machine: Machine, card: HTMLElement) => {
      void recordTrackedEvent({
        eventType: "machine_selected",
        machineId: machine.id,
        payload: {
          machineType: machine.machineType,
          storePlace: machine.storePlace,
        },
      });
      const chosen = toFlowMachine(machine);
      morph.from(card);
      // rendered right away, not batched with the next step: the badge shows
      // up (the card starts flying), and the grid slides out without the card
      flushSync(() => setStep({ name: "machine", chosen }));
      // the next step slides in while the card is still flying
      setStep(
        needsDryerBundle(machine)
          ? { name: "bundle", machine: chosen }
          : { name: "prepare", choice: { machine: chosen, dryerBundle: null } },
      );
    },
    chooseBundle: (machine: FlowMachineWithId, dryerBundle: ChosenDryerBundle) =>
      setStep({ name: "prepare", choice: { machine, dryerBundle } }),
    // the payment is created only once the machine is ready (a reservation
    // comes with its own)
    prepared: (choice: Choice, reserved?: Purchase) =>
      reserved ? setStep({ name: "select", purchase: reserved }) : select(choice),
    // back to the dryer program or the machine: a reservation's payment is dropped
    backFromPrepare: (choice: Choice, reserved?: Purchase) => {
      if (reserved) {
        removePayment.mutate({
          key: reserved.payment.kioskPaymentNonceKey,
          cause: "cancelled-by-user",
        });
      }
      setStep(
        choice.dryerBundle
          ? { name: "bundle", machine: choice.machine }
          : { name: "machine" },
      );
    },
    // the payment was removed or needs the coupon: create it again
    reload: select,
    start: async (purchase: Purchase, method: PaymentMethod) => {
      if (method === "card") {
        // the terminal takes over while apply-payment waits for it
        setStep({ name: "card", purchase });
        if (await apply(purchase, "KIOSK_BANK_CARD")) paid(purchase);
      } else if (method === "balance") {
        checkBalance.mutate(purchase);
      } else if (method === "cash") {
        if (await apply(purchase, "KIOSK_CASH")) {
          setStep({ name: "cash", purchase });
        }
      } else if (await apply(purchase, "FULL_DISCOUNT")) {
        paid(purchase);
      }
    },
    confirmBalance: async (purchase: Purchase) => {
      if (await apply(purchase, "BALANCE")) paid(purchase);
    },
    paid,
    // back to the machine set up: this payment is dropped, going on creates
    // a new one
    backFromSelect: (purchase: Purchase) => {
      removePayment.mutate({
        key: purchase.payment.kioskPaymentNonceKey,
        cause: "cancelled-by-user",
      });
      setStep({
        name: "prepare",
        choice: { machine: purchase.machine, dryerBundle: purchase.dryerBundle },
      });
    },
    exit: () => navigate({ to: "/machine-type-selector-screen" }),
  };
}

/** The header for the step: the machine (once chosen) and the title. */
function stepHeader(
  kind: FlowMachineKind,
  step: Step,
  status: OrderStatus | undefined,
  startTimedOut: boolean,
) {
  const choice =
    step.name === "prepare"
      ? step.choice
      : "purchase" in step
        ? step.purchase
        : null;
  return {
    machine:
      step.name === "machine"
        ? (step.chosen ?? null)
        : step.name === "bundle"
          ? step.machine
          : (choice?.machine ?? null),
    programMinutes: choice?.dryerBundle?.minutes,
    ...stepTitle(kind, step, status, startTimedOut),
  };
}

function stepTitle(
  kind: FlowMachineKind,
  step: Step,
  status: OrderStatus | undefined,
  startTimedOut: boolean,
) {
  if (step.name === "machine") {
    return {
      title:
        kind === "dryer" ? (
          <Trans>Válaszd ki a szárítógépet!</Trans>
        ) : (
          <Trans>Válaszd ki a mosógépet!</Trans>
        ),
      titleKey: "machine",
    };
  }
  if (step.name === "bundle") {
    return {
      title: <Trans>Válaszd ki a szárítás idejét!</Trans>,
      titleKey: "bundle",
    };
  }
  if (step.name === "select") {
    return {
      title:
        step.purchase.payment.price === 0 ? (
          <Trans>Indíthatod a gépet!</Trans>
        ) : (
          <Trans>Válassz fizetési módot!</Trans>
        ),
      titleKey: "select",
    };
  }
  if (step.name === "coupon") {
    return { title: <Trans>Van kuponod?</Trans>, titleKey: "coupon" };
  }
  if (step.name === "prepare") {
    return guideHeader("prepare");
  }
  if (step.name === "card") {
    return {
      title: <Trans>Kérjük fizess bankkártyával!</Trans>,
      titleKey: "card",
    };
  }
  if (step.name === "paid") {
    // same header as the guide that follows: a "Köszönjük!" here reads as done
    return guideHeader("starting");
  }
  if (step.name === "cash" || step.name === "balance") {
    return { title: <Trans>Kérjük fizess!</Trans>, titleKey: "pay" };
  }
  const isRunning = status?.spinningState === "spinning";
  const failed =
    (!!status && isStartFailed(status)) || (startTimedOut && !isRunning);
  return guideHeader(guideStatus("after-prepare", isRunning, failed));
}

/** A payment step's content: an optional back arrow in the header, the footer below. */
function StepBody({
  back,
  footer,
  children,
}: {
  back?: { onClick: () => void; label?: string };
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    // room for the fixed footer
    <div className="relative flex-1 flex flex-col mt-14 pb-40">
      {back && <FlowBack onClick={back.onClick} label={back.label} />}
      {children}
      {/* out of the sliding step, so the footer stays put while it slides */}
      {footer && createPortal(footer, document.body)}
    </div>
  );
}

function MachineStep({
  kind,
  chosen,
  onChoose,
  onBack,
}: {
  kind: FlowMachineKind;
  chosen: FlowMachineWithId | undefined;
  onChoose: (machine: Machine, card: HTMLElement) => void;
  onBack: () => void;
}) {
  useAudioSlot(
    kind === "dryer"
      ? "page.machine-selector.drying"
      : "page.machine-selector.washing",
  );
  return (
    <StepBody
      // nothing to go back to while the chosen card is flying
      back={chosen ? undefined : { onClick: onBack }}
      footer={<Footer helpText={<MachineSelectHelpText />} />}
    >
      <MachineGrid
        machineType={kind === "dryer" ? "drying-machines" : "washing-machines"}
        // one machine at a time
        onSelect={chosen ? () => {} : onChoose}
        // it's on its way into the header
        hiddenMachineId={chosen?.id}
        accentColors
      />
    </StepBody>
  );
}

function BundleStep({
  machineId,
  onChoose,
  onBack,
}: {
  machineId: number;
  onChoose: (bundle: ChosenDryerBundle) => void;
  onBack: () => void;
}) {
  useAudioSlot("page.dryer-bundle-selector");
  return (
    <StepBody
      back={{ onClick: onBack }}
      footer={<Footer helpText={<MachineSelectHelpText />} />}
    >
      <DryerBundleChoice machineId={machineId} onSelect={onChoose} />
    </StepBody>
  );
}

function SelectStep({
  onBack,
  ...choice
}: Parameters<typeof PaymentMethodChoice>[0] & { onBack: () => void }) {
  useAudioSlot("page.payment-selector");
  return (
    <StepBody
      back={{ onClick: onBack }}
      footer={<Footer helpText={<PaymentMethodHelpText />} />}
    >
      <OfflineWarning />
      <PaymentMethodChoice {...choice} />
    </StepBody>
  );
}

function CouponStep({
  onBack,
  ...form
}: Parameters<typeof CouponCodeForm>[0] & { onBack: () => void }) {
  useAudioSlot("page.coupon");
  return (
    <StepBody
      back={{ onClick: onBack }}
      footer={<Footer helpText={<CouponHelpText />} />}
    >
      <CouponCodeForm {...form} showTitle={false} />
    </StepBody>
  );
}

/** Before paying: the washer's door and program, or the dryer's door and temperature. */
function PrepareStep({
  machine,
  onPrepared,
  onBack,
}: {
  machine: FlowMachine;
  onPrepared: () => void;
  onBack: () => void;
}) {
  const guide = {
    phase: "prepare" as const,
    machineNumber: machine.number,
    machineCapacity: machine.capacity,
    isRunning: false,
    sessionId: "",
    onPrepared,
    onBack,
    // nothing is paid yet, so nothing can time out
    onStartTimeout: () => {},
  };
  return (
    // the machine scene needs the room: start up in the header's wave
    <div className={cn("flex-1 flex flex-col", FLOW_HEADER_OVERLAP)}>
      {machine.kind === "dryer" ? (
        <DryStartGuideContent {...guide} />
      ) : (
        <WashStartGuideContent {...guide} />
      )}
    </div>
  );
}

function CashStep({
  payment,
  onPaid,
  onBackToSelector,
}: {
  payment: PaymentType;
  onPaid: () => void;
  onBackToSelector: () => void;
}) {
  const { t } = useTranslation();
  const cash = useCashPayment(payment, { enabled: true });
  const leaving = useStepLeaving();
  // the cash-agent's polled status says when it's paid; there's no event to hook
  useEffect(() => {
    if (cash.isPaid) onPaid();
  }, [cash.isPaid]);

  return (
    <StepBody
      back={
        cash.canAbort
          ? {
              onClick: () => cash.abort.mutate(),
              label: t("Megszakítás"),
            }
          : undefined
      }
      footer={<CashPaymentFooter cash={cash} />}
    >
      {/* out of the sliding step, so they cover the whole screen even while
          it slides in; gone as soon as it slides out */}
      {!leaving &&
        createPortal(
          <CashPaymentOutcomeModals
            cash={cash}
            payment={payment}
            onPaid={onPaid}
            onBackToSelector={onBackToSelector}
          />,
          document.body,
        )}
      <CashPaymentBody cash={cash} payment={payment} heading="status-only" />
    </StepBody>
  );
}

/** After payment: the washer or dryer start guide, fed by the order status poll. */
function StartGuide({
  machine,
  payment,
  status,
  timedOut,
  onStartTimeout,
  onPaidBeatEnd,
}: {
  machine: FlowMachine;
  payment: PaymentType;
  status: OrderStatus;
  timedOut: boolean;
  onStartTimeout: () => void;
  onPaidBeatEnd?: () => void;
}) {
  const navigate = useNavigate();
  const { data: shop } = useShop();
  // fixed at the first answer, so the countdown bar doesn't jump on every poll
  const [startTimeoutSeconds] = useState(
    status.startSignalRemainingSeconds ?? START_WINDOW_SECONDS,
  );
  const isRunning = status.spinningState === "spinning";
  // today's invoice-question-screen rules
  const canRequestInvoice =
    !getToken() && payment.price > 0 && !!shop.canGenerateInvoices;
  const guide = {
    machineNumber: machine.number,
    machineCapacity: machine.capacity,
    isRunning,
    failed: isStartFailed(status) || (timedOut && !isRunning),
    startTimeoutSeconds,
    onStartTimeout,
    onPaidBeatEnd,
    sessionId: status.sessionId ?? "",
    collectorsDiscount: status.collectorsDiscount,
    currencySymbol: shop.currency,
    invoice: canRequestInvoice
      ? {
          onRequest: () =>
            navigate({
              to: "/invoice-generation-screen/$nonce",
              params: { nonce: payment.kioskPaymentNonceKey },
              search: { then: "start" },
            }),
        }
      : undefined,
    onStartAnother: () => {
      sessionStorage.removeItem("coupon");
      navigate({ to: "/machine-type-selector-screen" });
    },
    onFinish: () => navigate({ to: "/start-screen", reloadDocument: true }),
  };

  return (
    // like the prepare steps: the scene needs the room
    <div className={cn("flex-1 flex flex-col", FLOW_HEADER_OVERLAP)}>
      {/* one slot at a time: the guide's clip would stay silent under the
          success clip */}
      {onPaidBeatEnd ? (
        <AudioSlot slot="modal.cash-payment.success" />
      ) : (
        machine.kind !== "dryer" && <AudioSlot slot={audioSlotFor(status)} />
      )}
      {machine.kind === "dryer" ? (
        <DryStartGuideContent phase="after-prepare" {...guide} />
      ) : (
        <WashStartGuideContent phase="after-prepare" {...guide} />
      )}
    </div>
  );
}
